import React from 'react';
import ChecklistOptionHeader from 'components/common/headers/ChecklistOptionHeader';
import { CARD_DATA } from 'global/checklists-helpers/ChecklistHelper';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { ErrorOutline, InfoButton, WarningFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { colors } from '@insurancemenu/imenu-components-lib';
import { toJS } from 'mobx';
import { EnrollmentStage, EnrollmentType } from 'types/enrollmentService';
import clsx from 'clsx';
import { createStyles } from './styles';

const ReviewSubmitHeader = observer((): JSX.Element => {
  const {
    domainStoreV2: { domainHeader },
    reviewSubmitStore: {
      enrollmentModule,
      daysLeft,
      isSubmitNotEligible,
      isUwRulesNotPass,
      electionsCheck,
      isElectionValid,
      enrollmentPeriodCannotBeResubmitted,
    },
  } = useRootStore();

  const period = toJS(enrollmentModule.enrollmentCoreData);
  const convertedDomain = toJS(domainHeader);
  const {
    isProductLinesQuestionAnswered,
    isWaiverQuestionAnswered,
    subsWithClass,
    allWithClasses,
    isDependentOnProductLineValid,
    generalQuestionAnswered,
  } = toJS(electionsCheck);

  const areClassesValid = subsWithClass === 0 || (convertedDomain.properties.eeClassesRequired ? allWithClasses : true);

  const classes = createStyles();

  const periodDependText = (): JSX.Element => {
    switch (period?.type) {
      case EnrollmentType.RENEWAL:
        return (
          <p>
            There are <b>{daysLeft} days left</b> to submit this group for the renewal effective date:{' '}
            <b>{dateFormatters.normDateToShortAmericanDate(period.effectiveDate)}</b>
          </p>
        );
      case EnrollmentType.NEW_SALE:
      default:
        return (
          <p>
            There are <b>{daysLeft} days left</b> to submit this group for the selected effective date:{' '}
            <b>{dateFormatters.normDateToShortAmericanDate(period?.effectiveDate || '')}</b>
          </p>
        );
    }
  };

  const daysLeftText = (): JSX.Element => {
    if (isUwRulesNotPass) {
      return (
        <>
          <ErrorOutline width="5.6rem" className={classes.icon} color={colors.red[600]} />
          <p>
            Group does not meet underwriting guidelines and is <b>not eligible to enroll.</b> Please contact your
            account representative or remove this line in the confirmed proposal for this effective date.
          </p>
        </>
      );
    }
    switch (daysLeft) {
      case 0:
        return isSubmitNotEligible || enrollmentPeriodCannotBeResubmitted ? (
          <>
            <ErrorOutline width="5.6rem" className={classes.icon} color={colors.red[600]} />
            <p>
              Please note that the <b>cut off for this effective date has passed</b>. You can either start a new
              enrollment for the next available effective date for this group or speak with your carrier
              representative/account manager.
            </p>
          </>
        ) : (
          <>
            <WarningFilled width="5.6rem" color={colors.red[500]} />
            <p>
              Please note that the <b>cut off for this effective date has passed</b>. You can still generate the
              enrollment packet but you will need to work with your representative regarding your original effective
              date.
            </p>
          </>
        );
      case 1:
        return enrollmentPeriodCannotBeResubmitted ? (
          <>
            <ErrorOutline width="5.6rem" className={classes.icon} color={colors.red[600]} />
            <p>
              This enrollment for effective date{' '}
              <b>{dateFormatters.normDateToShortAmericanDate(period?.effectiveDate || '')}</b> has already been
              submitted. Please contact your carrier representative if you feel it has been submitted in error or you
              need to make a change.
            </p>
          </>
        ) : (
          <>
            <WarningFilled width="5.6rem" color={colors.orange[600]} />
            <p>
              <b>Today is the final day to submit this group.</b> Please review and submit before 5pm EST to allow for
              processing.
            </p>
          </>
        );
      default:
        return enrollmentPeriodCannotBeResubmitted ? (
          <>
            <ErrorOutline width="5.6rem" className={classes.icon} color={colors.red[600]} />
            <p>
              This enrollment for effective date{' '}
              <b>{dateFormatters.normDateToShortAmericanDate(period?.effectiveDate || '')}</b> has already been
              submitted. Please contact your carrier representative if you feel it has been submitted in error or you
              need to make a change.
            </p>
          </>
        ) : (
          <>
            <InfoButton width="5.6rem" color={colors.green[600]} />
            {periodDependText()}
          </>
        );
    }
  };

  const notEditableHeader = (): JSX.Element => {
    switch (period?.stage) {
      case EnrollmentStage.ARCHIVED:
        return (
          <>
            <ErrorOutline width="5.6rem" color={colors.red[600]} />
            <p>
              This group has been archived. If you want to start working with this group again, please go back to the
              dashboard and unarchive this group. Once unarchived, it’ll show up in the active groups list under All
              Groups.
            </p>
          </>
        );
      case EnrollmentStage.SOLD:
        return (
          <>
            <ErrorOutline width="5.6rem" color={colors.red[600]} />
            <p>
              This group has been sold for the effective date:
              <b>{dateFormatters.normDateToShortAmericanDate(period.effectiveDate)}</b>. You can view the enrollment
              packet from the group profile page. If you need to make any changes, please contact your carrier rep.
            </p>
          </>
        );
      default:
        return (
          <>
            <ErrorOutline width="5.6rem" color={colors.red[600]} />
            <p>Due to carrier limitations, resubmitting is not available for this catalog.</p>
          </>
        );
    }
  };

  return (
    <>
      <ChecklistOptionHeader
        title={CARD_DATA.REVIEW_SUBMIT.title}
        subtitle={CARD_DATA.REVIEW_SUBMIT.text}
        tutorialLink={CARD_DATA.REVIEW_SUBMIT.tutorialLink}
      />
      {!isElectionValid && (
        <div className={clsx(classes.contentWrapper, classes.validationContentWrapper)}>
          <div className={classes.headerWarning}>
            <h3 className={classes.validationErrorTitle}>Attention! There are issues with your submission.</h3>
            <ul className={classes.validationErrorsList}>
              {!isProductLinesQuestionAnswered && (
                <li>
                  Additional information still missing for product lines that have been elected. Check employee
                  elections page.
                </li>
              )}
              {!isWaiverQuestionAnswered && <li>There are waivers forms that still need to be completed.</li>}
              {!areClassesValid && <li>All subscribers must have classes.</li>}
              {!isDependentOnProductLineValid && <li>Some employees still need to be elected into Product Line</li>}
              {!generalQuestionAnswered && <li>There are employees with incomplete profile.</li>}
            </ul>
          </div>
        </div>
      )}
      {period?.effectiveDate && !enrollmentPeriodCannotBeResubmitted && (
        <div className={classes.contentWrapper}>
          <div className={classes.headerWarning}>{daysLeftText()}</div>
        </div>
      )}
      {enrollmentPeriodCannotBeResubmitted && (
        <div className={classes.contentWrapper}>
          <div className={classes.headerWarning}>{notEditableHeader()}</div>
        </div>
      )}
    </>
  );
});

export default ReviewSubmitHeader;
